import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Presentation,
  Trash2,
} from 'lucide-react';
import femaLogo from 'assets/images/fema-logo.png';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  PLANNING_PHASES,
  widgetsHiddenForFamily,
} from 'components/IAP/planningView/USCGPage';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import {
  RenderWidget,
  buildFormsDropdownOptions,
  useWidgetRenderContext,
} from 'components/IAP/planningView/widgetRegistry';
import {
  useSitrepV2,
  useSitrepsV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import SitrepDocumentView from 'components/Reports/document/SitrepDocumentView';
import { SitrepDynamicDataProvider } from 'components/Reports/section/SitrepDynamicDataContext';
import { SitStatDragHandle } from 'components/MapPage/CorporateViewTabs/SitStatTab/SitStatDragHandle';
import {
  listSitStatTemplates,
  resolveSitStatStorageKey,
} from 'components/MapPage/CorporateViewTabs/SitStatTab/sitstat-layout-storage';
import type { SitStatTemplate } from 'components/MapPage/CorporateViewTabs/SitStatTab/sitstat-template-types';
import { nestSitrepSections } from 'components/Reports/document/nestSitrepSections';
import { partitionPublishedSitreps } from 'components/Reports/utils/partitionPublishedSitreps';
import { useFemaOpsBriefingFlag } from 'utils/featureFlags';

import {
  FEMA_OPS_PRESENTATION_NAME,
  FEMA_OPS_PRESENTATION_SOURCE,
  isUnsetSitstat,
  isUneditedFemaSeed,
  buildFemaOpsBriefing,
} from './femaOpsBriefing';
import { useEnsureFemaOpsBriefing } from './femaOpsSeed';
import { PresentedReportFrame } from './presentedReportScope';
import {
  BriefingStage,
  CoverStage,
  ReportDocumentStage,
  ReportWidgetsStage,
  ViewStage,
} from './PresentationStages';
import {
  useCreateHubPresentation,
  useDeleteHubPresentation,
  useHubPresentations,
  useIncidentPresentation,
  useSaveHubPresentation,
  useSaveIncidentPresentation,
  type PresentationSlide,
} from './presentationHooks';
import {
  applySlideSource,
  decodeSource,
  encodeSource,
  isPresentableReport,
  newSlide,
  reportSlideLabel,
} from './presentationSources';

const QUAD_PAGE_SIZE = 4;

function reportLayoutChoice(slide: PresentationSlide): 'document' | 'quad' {
  return slide.layout === 'quad' ? 'quad' : 'document';
}

const REPORT_LAYOUT_LABEL = {
  document: 'Document',
  quad: 'Quad Chart',
} as const;

function slideWithReportLayout(
  slide: PresentationSlide,
  layout: 'document' | 'quad'
): PresentationSlide {
  const {
    widgetCount: _widgetCount,
    widgetOffset: _widgetOffset,
    ...rest
  } = slide;
  return { ...rest, layout };
}

function SortableSlideRow({
  slide,
  index,
  options,
  onChange,
  onRemove,
}: {
  slide: PresentationSlide;
  index: number;
  options: { value: string; label: string }[];
  onChange: (next: PresentationSlide) => void;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });
  const [notes, setNotes] = useState(slide.notes);
  const [body, setBody] = useState(slide.body ?? '');
  const [eyebrow, setEyebrow] = useState(slide.eyebrow ?? '');
  const [title, setTitle] = useState(slide.title ?? '');
  const [dateLine, setDateLine] = useState(slide.dateLine ?? '');
  const [timeLine, setTimeLine] = useState(slide.timeLine ?? '');
  const selected = encodeSource(slide);
  const rowOptions = options.some((option) => option.value === selected)
    ? options
    : [{ value: selected, label: slide.title || 'Briefing' }, ...options];

  useEffect(() => {
    setNotes(slide.notes);
    setBody(slide.body ?? '');
    setEyebrow(slide.eyebrow ?? '');
    setTitle(slide.title ?? '');
    setDateLine(slide.dateLine ?? '');
    setTimeLine(slide.timeLine ?? '');
  }, [slide]);

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={`tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-3 ${
        isDragging ? 'tw:opacity-40' : ''
      }`}
    >
      <div className="tw:flex tw:items-center tw:gap-2">
        <SitStatDragHandle
          ariaLabel={`Reorder slide ${index + 1}`}
          isDragging={isDragging}
          setActivatorNodeRef={setActivatorNodeRef}
          attributes={attributes}
          listeners={listeners}
        />
        <span className="tw:text-sm tw:font-medium">Slide {index + 1}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="tw:ml-auto"
          aria-label={`Remove slide ${index + 1}`}
          onClick={onRemove}
        >
          <Trash2 className="tw:size-4" />
        </Button>
      </div>
      {slide.kind === 'blank' ? (
        <p className="tw:text-sm tw:font-medium">Blank slide</p>
      ) : (
        <Select
          value={selected}
          onValueChange={(value) => {
            if (!value) return;
            const next = decodeSource(value);
            if (!next) return;
            onChange(applySlideSource(slide, next));
          }}
        >
          <SelectTrigger aria-label={`Screen for slide ${index + 1}`}>
            <SelectValue placeholder="Choose a screen" />
          </SelectTrigger>
          <SelectContent className="dark">
            {rowOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {slide.kind === 'sitstat' ? (
        <Select
          value={
            slide.viewLayout === 'quad' || slide.viewLayout === 'framed'
              ? slide.viewLayout
              : 'current'
          }
          onValueChange={(value) => {
            if (value !== 'current' && value !== 'quad' && value !== 'framed') {
              return;
            }
            onChange({ ...slide, viewLayout: value });
          }}
        >
          <SelectTrigger aria-label={`Layout for slide ${index + 1}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="dark">
            <SelectItem value="current">Current</SelectItem>
            <SelectItem value="quad">Quad chart</SelectItem>
            <SelectItem value="framed">
              Current with top and bottom rails
            </SelectItem>
          </SelectContent>
        </Select>
      ) : null}
      {slide.kind === 'report' ? (
        <Select
          value={reportLayoutChoice(slide)}
          onValueChange={(value) => {
            if (value !== 'document' && value !== 'quad') return;
            onChange(slideWithReportLayout(slide, value));
          }}
        >
          <SelectTrigger aria-label={`Layout for slide ${index + 1}`}>
            <SelectValue>
              {REPORT_LAYOUT_LABEL[reportLayoutChoice(slide)]}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="dark">
            <SelectItem value="document">
              {REPORT_LAYOUT_LABEL.document}
            </SelectItem>
            <SelectItem value="quad">{REPORT_LAYOUT_LABEL.quad}</SelectItem>
          </SelectContent>
        </Select>
      ) : null}
      {slide.kind === 'cover' ? (
        <div className="tw:grid tw:grid-cols-2 tw:gap-2">
          <Input
            value={eyebrow}
            placeholder="Eyebrow"
            aria-label={`Eyebrow for slide ${index + 1}`}
            onChange={(event) => setEyebrow(event.target.value)}
            onBlur={() => onChange({ ...slide, eyebrow })}
          />
          <Input
            value={title}
            placeholder="Title"
            aria-label={`Title for slide ${index + 1}`}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={() => onChange({ ...slide, title })}
          />
          <Input
            value={dateLine}
            placeholder="Date"
            aria-label={`Date for slide ${index + 1}`}
            onChange={(event) => setDateLine(event.target.value)}
            onBlur={() => onChange({ ...slide, dateLine })}
          />
          <Input
            value={timeLine}
            placeholder="Time"
            aria-label={`Time for slide ${index + 1}`}
            onChange={(event) => setTimeLine(event.target.value)}
            onBlur={() => onChange({ ...slide, timeLine })}
          />
        </div>
      ) : null}
      {slide.kind === 'blank' ? (
        <div
          onBlur={(event) => {
            const nextTarget = event.relatedTarget;
            if (
              nextTarget instanceof Node &&
              event.currentTarget.contains(nextTarget)
            ) {
              return;
            }
            if (body !== (slide.body ?? '')) onChange({ ...slide, body });
          }}
        >
          <TiptapRichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Slide content"
            minHeight="180px"
          />
        </div>
      ) : null}
      <Textarea
        value={notes}
        placeholder="Speaker notes"
        aria-label={`Speaker notes for slide ${index + 1}`}
        rows={3}
        onChange={(event) => setNotes(event.target.value)}
        onBlur={() => {
          if (notes !== slide.notes) onChange({ ...slide, notes });
        }}
      />
    </li>
  );
}

function FormStage({ sourceId }: { sourceId: string }) {
  const ctx = useWidgetRenderContext(PLANNING_PHASES, 0);
  return <RenderWidget widgetName={sourceId} ctx={ctx} />;
}

function SlideStage({
  slide,
  templates,
  report,
  reportSectionIndex,
  besideHubMap,
}: {
  slide: PresentationSlide;
  templates: SitStatTemplate[];
  report: ReturnType<typeof useSitrepV2>['data'];
  reportSectionIndex: number;
  besideHubMap: boolean;
}) {
  if (slide.kind === 'blank') {
    return (
      <div className="tw:h-full tw:min-h-0 tw:overflow-auto tw:bg-white tw:p-6">
        <TiptapRichTextEditor
          className="tiptap-editor--on-light"
          value={slide.body ?? ''}
          readOnly
          showToolbar={false}
          minHeight="100%"
        />
      </div>
    );
  }
  if (slide.kind === 'cover') {
    return <CoverStage slide={slide} />;
  }
  if (slide.kind === 'briefing') {
    return <BriefingStage slide={slide} embedMap={!besideHubMap} />;
  }
  if (slide.kind === 'report') {
    if (!report) {
      return (
        <p className="tw:p-6 tw:text-sm tw:text-muted-foreground">
          Loading report…
        </p>
      );
    }
    const presentedReport = {
      ...report,
      approval_status: report.approval_status ?? 'APPROVED',
    };
    const sections = nestSitrepSections(presentedReport.sections ?? []);
    const reportStage =
      slide.layout === 'widgets' ? (
        <ReportWidgetsStage
          sitrep={presentedReport}
          sections={sections}
          offset={slide.widgetOffset ?? 0}
          count={slide.widgetCount === 4 ? 4 : 1}
        />
      ) : slide.layout === 'quad' ? (
        <ReportWidgetsStage
          sitrep={presentedReport}
          sections={sections}
          offset={reportSectionIndex * QUAD_PAGE_SIZE}
          count={4}
        />
      ) : besideHubMap ? (
        <div className="tw:h-full tw:min-h-0 tw:overflow-hidden">
          <SitrepDocumentView
            sitrep={presentedReport}
            sections={presentedReport.sections ?? []}
            canEdit={false}
            onRefetch={() => undefined}
          />
        </div>
      ) : (
        <ReportDocumentStage
          sitrep={presentedReport}
          section={sections[reportSectionIndex]}
        />
      );
    return (
      <SitrepDynamicDataProvider
        approvalStatus={presentedReport.approval_status}
        sections={presentedReport.sections}
      >
        <PresentedReportFrame sitrep={presentedReport}>
          {reportStage}
        </PresentedReportFrame>
      </SitrepDynamicDataProvider>
    );
  }
  if (slide.kind === 'form') {
    return <FormStage sourceId={slide.sourceId} />;
  }
  if (isUnsetSitstat(slide.sourceId)) {
    return (
      <p className="tw:p-6 tw:text-sm tw:text-muted-foreground">
        Choose a view for this slide.
      </p>
    );
  }
  const template = templates.find((entry) => entry.id === slide.sourceId);
  if (!template) {
    return (
      <p className="tw:p-6 tw:text-sm tw:text-muted-foreground">
        This view is not available on this browser.
      </p>
    );
  }
  return (
    <ViewStage
      template={template}
      viewLayout={
        slide.viewLayout === 'quad' || slide.viewLayout === 'framed'
          ? slide.viewLayout
          : 'current'
      }
    />
  );
}

export function PresentationPage({
  scope = 'incident',
}: {
  scope?: 'incident' | 'hub';
}) {
  const { incidentId } = useParams<{ incidentId: string }>();
  const groupGuid = useAppSelector(selectGroupGuid);
  const femaOps = useFemaOpsBriefingFlag();
  const ensureFemaRecords = useEnsureFemaOpsBriefing();
  const incidentQuery = useIncidentPresentation(
    scope === 'incident' ? incidentId : undefined
  );
  const hubGroupGuid = scope === 'hub' ? (groupGuid ?? undefined) : undefined;
  const hubQuery = useHubPresentations(hubGroupGuid);
  const presentations = useMemo(
    () => hubQuery.data?.presentations ?? [],
    [hubQuery.data]
  );
  const incidentSave = useSaveIncidentPresentation(
    scope === 'incident' ? incidentId : undefined
  );
  const hubSave = useSaveHubPresentation(hubGroupGuid);
  const hubCreate = useCreateHubPresentation(hubGroupGuid);
  const hubDelete = useDeleteHubPresentation(hubGroupGuid);
  const saveHub = hubSave.mutate;
  const createHub = hubCreate.mutate;
  const deleteHub = hubDelete.mutate;
  const saveIncident = incidentSave.mutate;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deckName, setDeckName] = useState('');
  const [slides, setSlides] = useState<PresentationSlide[] | null>(null);
  const loadedDeckId = useRef<string | null>(null);
  const femaSeedStarted = useRef(false);
  const femaGateLogged = useRef(false);
  const [femaSeedFailed, setFemaSeedFailed] = useState(false);
  const [presentIndex, setPresentIndex] = useState<number | null>(null);
  const [sectionCursor, setSectionCursor] = useState(0);
  const [enterAtEnd, setEnterAtEnd] = useState(false);
  const [notesOpen, setNotesOpen] = useState(true);
  const sitrepsQuery = useSitrepsV2();

  const storageKey = useMemo(
    () => resolveSitStatStorageKey(groupGuid ?? null),
    [groupGuid]
  );
  const templates = useMemo(
    () => listSitStatTemplates(storageKey),
    [storageKey]
  );
  const formFamily = useIcsFormFamily();
  const formOptions = useMemo(
    () =>
      buildFormsDropdownOptions({
        exclude: widgetsHiddenForFamily(formFamily),
      }),
    [formFamily]
  );
  const currentReports = useMemo(() => {
    if (scope !== 'hub') return [];
    const published = (sitrepsQuery.data ?? []).filter(
      (sitrep) =>
        isPresentableReport(sitrep) &&
        sitrep.is_sitrep &&
        !sitrep.archived &&
        (sitrep.approval_status === 'APPROVED' ||
          sitrep.approval_status == null)
    );
    return [
      ...partitionPublishedSitreps(published).latestSitreps.values(),
    ].sort((a, b) => reportSlideLabel(a).localeCompare(reportSlideLabel(b)));
  }, [scope, sitrepsQuery.data]);
  const sourceOptions = useMemo(() => {
    const cover = [{ value: 'cover|cover', label: 'Cover slide' }];
    const sitstats = templates.map((template) => ({
      value: encodeSource({ kind: 'sitstat', sourceId: template.id }),
      label: `View · ${template.name}`,
    }));
    if (scope === 'hub') {
      return [
        ...cover,
        ...currentReports.map((sitrep) => ({
          value: encodeSource({ kind: 'report', sourceId: sitrep.id }),
          label: reportSlideLabel(sitrep),
        })),
        ...sitstats,
      ];
    }
    return [
      ...cover,
      ...formOptions.map((option) => ({
        value: encodeSource({ kind: 'form', sourceId: option.value }),
        label: option.label,
      })),
      ...sitstats,
    ];
  }, [currentReports, formOptions, scope, templates]);

  const selected =
    presentations.find((presentation) => presentation.id === selectedId) ??
    null;
  const waitingForFema =
    scope === 'hub' &&
    femaOps &&
    hubQuery.isSuccess &&
    !femaSeedFailed &&
    presentations.length === 0;

  useEffect(() => {
    if (scope !== 'incident') return;
    if (!incidentQuery.isSuccess || slides !== null) return;
    setSlides(incidentQuery.data?.slides ?? []);
  }, [incidentQuery.data, incidentQuery.isSuccess, scope, slides]);

  useEffect(() => {
    if (scope !== 'hub' || !hubQuery.isSuccess) return;
    if (selectedId && presentations.some((entry) => entry.id === selectedId)) {
      return;
    }
    const next = presentations[0]?.id ?? null;
    if (next !== selectedId) setSelectedId(next);
  }, [hubQuery.isSuccess, presentations, scope, selectedId]);

  useEffect(() => {
    if (scope !== 'hub' || !selected) return;
    if (loadedDeckId.current === selected.id) return;
    loadedDeckId.current = selected.id;
    setSlides(selected.slides);
    setDeckName(selected.name);
    setPresentIndex(null);
    setSectionCursor(0);
  }, [scope, selected]);

  useEffect(() => {
    if (scope !== 'hub' || !hubQuery.isSuccess || !sitrepsQuery.isSuccess) {
      return;
    }
    if (!femaGateLogged.current && (!femaOps || femaSeedFailed)) {
      femaGateLogged.current = true;
    }
    if (!femaOps || femaSeedFailed) return;
    if (femaSeedStarted.current) return;
    femaSeedStarted.current = true;
    const existing = presentations.find(
      (presentation) => presentation.source === FEMA_OPS_PRESENTATION_SOURCE
    );
    const replaceSlides = !existing || isUneditedFemaSeed(existing.slides);
    const hadOthers = presentations.some(
      (presentation) => presentation.source !== FEMA_OPS_PRESENTATION_SOURCE
    );
    const savedSitstat = templates.find((template) => !template.isSystem)?.id;
    void ensureFemaRecords(sitrepsQuery.data ?? [])
      .then((records) => {
        if (!replaceSlides) {
          return;
        }
        const slides = buildFemaOpsBriefing({
          sitstatTemplateId: savedSitstat,
          nationalReportId: records.nationalReportId,
          regionReportIds: records.regionReportIds,
        });
        if (!existing) {
          createHub(
            {
              name: FEMA_OPS_PRESENTATION_NAME,
              source: FEMA_OPS_PRESENTATION_SOURCE,
              slides,
            },
            {
              onSuccess: (created) => {
                if (!hadOthers) setSelectedId(created.id);
              },
              onError: () => {
                setFemaSeedFailed(true);
              },
            }
          );
          return;
        }
        saveHub(
          { id: existing.id, slides },
          {
            onSuccess: () => {
              const loadedMatches = loadedDeckId.current === existing.id;
              if (loadedMatches) setSlides(slides);
            },
            onError: () => {
              setFemaSeedFailed(true);
            },
          }
        );
      })
      .catch(() => setFemaSeedFailed(true));
  }, [
    createHub,
    ensureFemaRecords,
    femaOps,
    femaSeedFailed,
    hubQuery.isSuccess,
    presentations,
    saveHub,
    scope,
    sitrepsQuery.data,
    sitrepsQuery.isSuccess,
    templates,
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const deck = slides ?? [];
  const presenting = presentIndex !== null ? deck[presentIndex] : undefined;
  const reportQuery = useSitrepV2(
    presenting?.kind === 'report' ? presenting.sourceId : undefined
  );
  const reportSections = useMemo(
    () => nestSitrepSections(reportQuery.data?.sections ?? []),
    [reportQuery.data?.sections]
  );
  const quadReport =
    presenting?.kind === 'report' && presenting.layout === 'quad';
  const stepsThroughReport =
    presenting?.kind === 'report' &&
    (quadReport ||
      (scope !== 'hub' &&
        presenting.layout !== 'quad' &&
        presenting.layout !== 'widgets'));
  const documentSteps = quadReport
    ? Math.max(Math.ceil(reportSections.length / QUAD_PAGE_SIZE), 1)
    : stepsThroughReport
      ? Math.max(reportSections.length, 1)
      : 1;
  const sectionIndex = Math.min(sectionCursor, documentSteps - 1);

  useEffect(() => {
    if (!enterAtEnd) return;
    if (
      presenting?.kind !== 'report' ||
      presenting.layout === 'widgets' ||
      (scope === 'hub' && presenting.layout !== 'quad')
    ) {
      setEnterAtEnd(false);
      return;
    }
    if (!reportQuery.isSuccess) return;
    const last =
      presenting.layout === 'quad'
        ? Math.max(Math.ceil(reportSections.length / QUAD_PAGE_SIZE) - 1, 0)
        : Math.max(reportSections.length - 1, 0);
    setSectionCursor(last);
    setEnterAtEnd(false);
  }, [
    enterAtEnd,
    presenting,
    reportQuery.isSuccess,
    reportSections.length,
    scope,
  ]);

  const persist = (next: PresentationSlide[]) => {
    setSlides(next);
    if (scope === 'hub') {
      if (selected) saveHub({ id: selected.id, slides: next });
      return;
    }
    if (incidentId) saveIncident(next);
  };

  const commitDeckName = () => {
    if (!selected) return;
    const trimmed = deckName.trim();
    if (!trimmed || trimmed === selected.name) {
      setDeckName(selected.name);
      return;
    }
    saveHub(
      { id: selected.id, slides: deck, name: trimmed },
      { onError: () => setDeckName(selected.name) }
    );
  };

  const createDeck = () => {
    createHub(
      {
        name: 'Presentation',
        slides: [newSlide('cover', 'cover')],
      },
      {
        onSuccess: (created) => {
          loadedDeckId.current = null;
          setSelectedId(created.id);
        },
      }
    );
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = deck.findIndex((slide) => slide.id === active.id);
    const to = deck.findIndex((slide) => slide.id === over.id);
    if (from < 0 || to < 0) return;
    persist(arrayMove(deck, from, to));
  };

  const mapCtx = useOptionalUscgMapContext();
  const mapApiRef = useRef(mapCtx);
  const savedMapVisibleRef = useRef<boolean | null>(null);
  const hideHubMap =
    scope === 'hub' &&
    presenting != null &&
    (presenting.kind === 'sitstat' ||
      (presenting.kind === 'report' &&
        (presenting.layout === 'quad' || presenting.layout === 'widgets')));
  const showHubMap =
    scope === 'hub' &&
    presenting != null &&
    ((presenting.kind === 'report' &&
      presenting.layout !== 'quad' &&
      presenting.layout !== 'widgets') ||
      (presenting.kind === 'briefing' && presenting.showMap === true));
  const presentingActive = presentIndex !== null;
  const hasMapApi = Boolean(mapCtx);

  useEffect(() => {
    mapApiRef.current = mapCtx;
  }, [mapCtx]);

  useEffect(() => {
    const mapApi = mapApiRef.current;
    if (scope !== 'hub' || !presentingActive || !mapApi) return;
    if (savedMapVisibleRef.current === null) {
      savedMapVisibleRef.current = mapApi.isMapVisible;
    }
    if (hideHubMap) mapApi.setIsMapVisible(false);
    else if (showHubMap) mapApi.setIsMapVisible(true);
    else mapApi.setIsMapVisible(savedMapVisibleRef.current);
  }, [hasMapApi, hideHubMap, presentingActive, scope, showHubMap]);

  useEffect(() => {
    if (scope !== 'hub') return;
    return () => {
      const mapApi = mapApiRef.current;
      if (savedMapVisibleRef.current === null || !mapApi) return;
      mapApi.setIsMapVisible(savedMapVisibleRef.current);
      savedMapVisibleRef.current = null;
    };
  }, [presentingActive, scope]);

  const goNext = () => {
    if (presentIndex === null) return;
    if (sectionIndex < documentSteps - 1) {
      setSectionCursor(sectionIndex + 1);
      return;
    }
    setPresentIndex(presentIndex + 1);
    setSectionCursor(0);
    setEnterAtEnd(false);
  };

  const goPrevious = () => {
    if (presentIndex === null) return;
    if (sectionIndex > 0) {
      setSectionCursor(sectionIndex - 1);
      return;
    }
    setPresentIndex(presentIndex - 1);
    setSectionCursor(0);
    setEnterAtEnd(true);
  };

  if (presenting && presentIndex !== null) {
    const atStart = presentIndex === 0 && sectionIndex === 0;
    const atEnd =
      presentIndex >= deck.length - 1 && sectionIndex >= documentSteps - 1;
    const stepLabel =
      presenting.kind === 'report' && documentSteps > 1
        ? `${presentIndex + 1} / ${deck.length} · ${sectionIndex + 1} / ${documentSteps}`
        : `${presentIndex + 1} / ${deck.length}`;
    const setPresentedReportLayout = (layout: 'document' | 'quad') => {
      if (presenting.kind !== 'report' || presenting.layout === layout) return;
      setSectionCursor(0);
      setEnterAtEnd(false);
      persist(
        deck.map((slide, index) =>
          index === presentIndex ? slideWithReportLayout(slide, layout) : slide
        )
      );
    };
    return (
      <div className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:bg-background tw:text-foreground">
        <div className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:py-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setPresentIndex(null);
              setSectionCursor(0);
              setEnterAtEnd(false);
            }}
          >
            Back to slides
          </Button>
          <span className="tw:text-sm tw:text-muted-foreground">
            {stepLabel}
          </span>
          {presenting.kind === 'report' ? (
            <Select
              value={reportLayoutChoice(presenting)}
              onValueChange={(value) => {
                if (value !== 'document' && value !== 'quad') return;
                setPresentedReportLayout(value);
              }}
            >
              <SelectTrigger aria-label="Report layout" className="tw:w-44">
                <SelectValue>
                  {REPORT_LAYOUT_LABEL[reportLayoutChoice(presenting)]}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value="document">
                  {REPORT_LAYOUT_LABEL.document}
                </SelectItem>
                <SelectItem value="quad">{REPORT_LAYOUT_LABEL.quad}</SelectItem>
              </SelectContent>
            </Select>
          ) : null}
          <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={atStart}
              onClick={goPrevious}
            >
              <ChevronLeft className="tw:size-4" />
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={atEnd}
              onClick={goNext}
            >
              Next
              <ChevronRight className="tw:size-4" />
            </Button>
          </div>
        </div>
        <div className="tw:relative tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden tw:p-3">
          <SlideStage
            slide={presenting}
            templates={templates}
            report={reportQuery.data}
            reportSectionIndex={sectionIndex}
            besideHubMap={scope === 'hub'}
          />
          {femaOps && presenting.kind === 'blank' ? (
            <img
              src={femaLogo}
              alt=""
              className="tw:pointer-events-none tw:absolute tw:bottom-5 tw:left-5 tw:z-10 tw:h-14 tw:w-auto"
            />
          ) : null}
        </div>
        {notesOpen ? (
          <div className="tw:border-t tw:border-border tw:bg-card tw:px-4 tw:py-3">
            <div className="tw:mb-1 tw:flex tw:items-center tw:justify-between">
              <span className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                Speaker notes
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setNotesOpen(false)}
              >
                Hide notes
              </Button>
            </div>
            <p className="tw:whitespace-pre-wrap tw:text-sm">
              {presenting.notes.trim()
                ? presenting.notes
                : 'No speaker notes for this slide.'}
            </p>
          </div>
        ) : (
          <div className="tw:border-t tw:border-border tw:px-3 tw:py-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setNotesOpen(true)}
            >
              Show notes
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:bg-background tw:text-foreground"
      data-tour="sitl-presentation-workspace"
    >
      <div className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:py-2">
        <Presentation className="tw:size-4" />
        {scope === 'hub' ? (
          <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2">
            {presentations.length > 0 ? (
              <Select
                value={selectedId ?? undefined}
                onValueChange={setSelectedId}
              >
                <SelectTrigger
                  className="tw:h-8 tw:w-56"
                  aria-label="Presentation"
                >
                  <SelectValue placeholder="Presentation" />
                </SelectTrigger>
                <SelectContent className="dark">
                  {presentations.map((presentation) => (
                    <SelectItem key={presentation.id} value={presentation.id}>
                      {presentation.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <h1 className="tw:text-sm tw:font-medium">Presentation</h1>
            )}
            <Input
              value={deckName}
              placeholder="Name"
              aria-label="Presentation name"
              disabled={!selected}
              className="tw:h-8 tw:w-56"
              onChange={(event) => setDeckName(event.target.value)}
              onBlur={commitDeckName}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={createDeck}
            >
              <Plus className="tw:size-4" />
              New presentation
            </Button>
            {selected && selected.source !== FEMA_OPS_PRESENTATION_SOURCE ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Delete presentation"
                onClick={() => {
                  const id = selected.id;
                  deleteHub(id, {
                    onSuccess: () => {
                      if (loadedDeckId.current === id) {
                        loadedDeckId.current = null;
                      }
                      setSelectedId(null);
                      setSlides(null);
                    },
                  });
                }}
              >
                <Trash2 className="tw:size-4" />
              </Button>
            ) : null}
          </div>
        ) : (
          <h1 className="tw:text-sm tw:font-medium">Presentation</h1>
        )}
        <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={scope === 'hub' && !selected}
            onClick={() =>
              persist([
                ...deck,
                scope === 'hub' || formOptions.length === 0
                  ? newSlide('cover', 'cover')
                  : newSlide('form', formOptions[0].value),
              ])
            }
          >
            <Plus className="tw:size-4" />
            Add slide
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={scope === 'hub' && !selected}
            onClick={() => persist([...deck, newSlide('blank', 'blank')])}
          >
            <Plus className="tw:size-4" />
            Blank slide
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={deck.length === 0}
            onClick={() => {
              setNotesOpen(true);
              setSectionCursor(0);
              setPresentIndex(0);
            }}
          >
            Present
          </Button>
        </div>
      </div>
      <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto tw:p-3">
        {scope === 'hub' && !groupGuid ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            Open an organization to edit its presentation.
          </p>
        ) : (scope === 'hub' &&
            Boolean(groupGuid) &&
            (hubQuery.isLoading ||
              waitingForFema ||
              (selected != null && slides === null))) ||
          (scope === 'incident' &&
            (incidentQuery.isLoading || slides === null)) ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            Loading presentation…
          </p>
        ) : scope === 'hub' && !selected ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            Create a presentation, then add a slide and write speaker notes.
          </p>
        ) : deck.length === 0 ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            {scope === 'hub'
              ? 'Add a slide, then choose a cover, a current report, or a view and write speaker notes.'
              : 'Add a slide, then choose a cover, an ICS form, or a view and write speaker notes.'}
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={deck.map((slide) => slide.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul
                className="tw:mx-auto tw:flex tw:max-w-2xl tw:flex-col tw:gap-3"
                data-tour="presentation-slides"
              >
                {deck.map((slide, index) => (
                  <SortableSlideRow
                    key={slide.id}
                    slide={slide}
                    index={index}
                    options={sourceOptions}
                    onChange={(next) =>
                      persist(
                        deck.map((entry) =>
                          entry.id === slide.id ? next : entry
                        )
                      )
                    }
                    onRemove={() =>
                      persist(deck.filter((entry) => entry.id !== slide.id))
                    }
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
